import { addDays, isoDate, parseLocal, dateOnly } from '../../lib/dates';
import { vaccineState } from '../bookings/domain';
import type { Booking } from '../bookings/types';

// The Needs attention derivation — the bookings arriving in the next 7 days that
// somebody has to chase before the dog turns up.
//
// It reads vaccine state from domain.vaccineState rather than parsing
// `vaccines_due` again: that free-text column is month-precision legacy data
// ("MARCH 23", "N/26+M/27"), the app already has ONE derivation for it, and a
// second would be free to drift from the flag strip the desk rows show.

// NO REQUESTS HERE (Freddi, 2026-10-14: "requested bookings should not appear in the needs attention
// bar as that should be reserved for viewings, unconfirmed bookings, and vaccines due"). A request
// reaches Patricia as a bubble beside "Daily" (notifications-daily-stack); it is not a stay yet, so
// nothing about it is chased here. Supersedes request-a-stay AC14/AC14a, which listed them first.
export type ViolationKind = 'missing' | 'lapsed' | 'unreadable' | 'unconf';

// Most severe first. A booking appears ONCE, under the worst thing wrong with
// it, but is counted under every reason it satisfies — one row, possibly two
// jobs. A missing certificate outranks a lapsed one (nothing to check at all
// beats something out of date), and any vaccine problem outranks an unconfirmed
// booking, because a deposit can be taken at the gate and a vaccination cannot.
export const SEVERITY: ViolationKind[] = ['missing', 'lapsed', 'unreadable', 'unconf'];

export interface AttentionItem {
  bookingId: string;
  checkIn: string; // ISO YYYY-MM-DD
  day: string; // 'Tue' — the chip's arrival label
  dogs: string; // 'Bracken, Paddy' — the whole household, for the tooltip
  lead: string; // 'Bracken' — the chip shows only the first
  owner: string;
  breed: string | null;
  kind: ViolationKind; // the most severe present
  unconfirmed: boolean; // carried separately: the counts need both axes
  vaccineIssue: boolean;
  vaccineText: string | null; // the raw record, for the booking editor's callout
}

// Statuses that mean the stay is no longer ahead of us. An arrived dog is
// already in its run: whatever its paperwork says, chasing it is a different
// job from the one this bar exists for.
// `declined` left the Daily with accept-or-decline (AC5): a request she said no
// to needs nothing more from anyone.
const CLOSED = new Set(['cancelled', 'collected', 'arrived', 'declined']);

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The check-in column is a date, but the API has been seen to hand back a
// timestamp; take the date part either way rather than trusting the shape.

/**
 * The look-ahead window for an anchor, plus the neighbours worth cache-warming.
 * Mirrors `dayStatWindows` — the desk prefetches its adjacent windows so a
 * date step resolves from cache instead of the network, and the bar had no
 * such thing: every step cost a real round trip with nothing on screen.
 */
export function lookaheadWindows(
  anchor: string,
  days = 7,
): { range: { from: string; to: string }; prefetch: { from: string; to: string }[] } {
  const win = (from: string) => ({ from, to: isoDate(addDays(from, days)) });
  return {
    range: win(anchor),
    prefetch: [win(isoDate(addDays(anchor, -1))), win(isoDate(addDays(anchor, 1)))],
  };
}

/**
 * THE selection rule — the one place that decides whether a booking needs
 * chasing. Returns the worst vaccine state across the household, or `null` when
 * the booking does not qualify at all.
 *
 * Extracted so the rail's counts and the panel's rows cannot describe different
 * sets: `deriveNeedsAttention` projects it into chips, `attentionBookings`
 * returns the rows themselves, and both ask this. A second predicate would be
 * free to drift, and the two surfaces sit inches apart on the page.
 */
function attentionState(
  b: Booking,
  from: string,
  to: string,
): { checkIn: string; unconfirmed: boolean; worstVaccine: ViolationKind | null } | null {
  if (CLOSED.has(b.status) || b.status === 'requested') return null;
  const checkIn = dateOnly(b.checkIn);
  if (checkIn < from || checkIn > to) return null;
  const unconfirmed = b.status === 'unconfirmed';
  let worstVaccine: ViolationKind | null = null;
  for (const dog of b.dogs ?? []) {
    const state = vaccineState(dog.vaccinesDue, checkIn);
    if (state === 'ok') continue;
    if (worstVaccine === null || SEVERITY.indexOf(state) < SEVERITY.indexOf(worstVaccine)) {
      worstVaccine = state;
    }
  }
  if (!unconfirmed && worstVaccine === null) return null;
  return { checkIn, unconfirmed, worstVaccine };
}

const byCheckIn = (a: { checkIn: string }, z: { checkIn: string }): number =>
  a.checkIn < z.checkIn ? -1 : a.checkIn > z.checkIn ? 1 : 0;

/**
 * The BOOKINGS needing attention in `[today, today + days]`, soonest arrival
 * first — whole rows, for the panel's shared stay-table, which needs the owner,
 * breed, area, rate and payment that the chip projection drops.
 */
export function attentionBookings(bookings: Booking[], todayIso: string, days = 7): Booking[] {
  const from = dateOnly(todayIso);
  const to = isoDate(addDays(from, days));
  return bookings
    .filter((b) => attentionState(b, from, to) !== null)
    .sort((a, z) => byCheckIn({ checkIn: dateOnly(a.checkIn) }, { checkIn: dateOnly(z.checkIn) }));
}

/**
 * Bookings needing attention in `[today, today + days]`, soonest arrival first.
 * `days` is inclusive of both ends — 7 means today plus the next seven days.
 */
export function deriveNeedsAttention(bookings: Booking[], todayIso: string, days = 7): AttentionItem[] {
  const from = dateOnly(todayIso);
  const to = isoDate(addDays(from, days));

  const items: AttentionItem[] = [];
  for (const b of bookings) {
    const state = attentionState(b, from, to);
    if (state === null) continue;
    const { checkIn, unconfirmed, worstVaccine } = state;

    const names = (b.dogs ?? []).map((d) => d.name).filter(Boolean);
    items.push({
      bookingId: b.id,
      checkIn,
      day: WEEKDAY[parseLocal(checkIn).getDay()]!,
      dogs: names.join(', '),
      lead: names[0] ?? '',
      owner: b.ownerContacts?.[0]?.name ?? '',
      breed: b.dogs?.[0]?.breed ?? null,
      kind: worstVaccine ?? 'unconf',
      unconfirmed,
      vaccineIssue: worstVaccine !== null,
      // The record the editor's callout quotes back. Taken from the dog that
      // set the severity, so "the record reads X" names the offending card.
      vaccineText: (b.dogs ?? []).find((d) => vaccineState(d.vaccinesDue, checkIn) === worstVaccine)?.vaccinesDue ?? null,
    });
  }

  return items.sort(byCheckIn);
}

// The bar's two counts. A booking that is both unconfirmed and short a
// vaccination is counted in BOTH — they are two separate telephone calls, and a
// total that hid one of them would understate the work.
export function countByReason(items: AttentionItem[]): { vaccinations: number; unconfirmed: number } {
  let vaccinations = 0;
  let unconfirmed = 0;
  for (const i of items) {
    if (i.vaccineIssue) vaccinations++;
    if (i.unconfirmed) unconfirmed++;
  }
  return { vaccinations, unconfirmed };
}

// MAY THE STRIP SAY "NOTHING TO CHASE"?
//
// An empty list means three different things — nothing is wrong, nothing has
// been read yet, or what has been read is about yesterday — and only the first
// is an all-clear. This has been got wrong twice, both times by a condition
// written inline where only one of the excuses was in view, so the rule has a
// name and a test now. A false all-clear is the one message an operator acts on
// by NOT acting.
//
// A VIEWING IS A FOURTH REASON THE DAY IS NOT CLEAR. Somebody coming to look
// round is work: no lapsed vaccination, no unpaid deposit AND nobody at the gate
// is what an all-clear now means. `viewings` is optional and defaults to none so
// that no existing caller is silently broken by the widening — the third excuse
// went into the NAMED function rather than into a condition at the call site,
// which is the whole reason this function has a name.
export function saysAllClear(s: {
  clear: boolean;
  loading?: boolean;
  stale?: boolean;
  viewings?: number;
}): boolean {
  return s.clear && !s.loading && !s.stale && !(s.viewings ?? 0);
}

// The word the viewings counter says. Kept beside anomalyWord rather than in
// viewings.ts because the segment reads both from one place, and the visual
// contract measures the strings production actually renders.
export function viewingWord(n: number): string {
  return `viewing${n === 1 ? '' : 's'}`;
}

// THE WORDS EVERY COUNTER SAYS. Exported so the visual contract measures the
// strings production actually renders: the width test built its own, drifted to
// a shorter pair when those were briefly tried, and went on asserting that
// everything fitted while the real labels were 15px wider (PR #166, codex P2).
//
// "VACCINES", NOT "VACCINATIONS" (Sir, 2026-08-26), which reverses PR #166. The
// long form was restored then because shortening it was a WIDTH trade, and the
// roster was made to pay for vocabulary it should not have paid for. With the
// rail collapsed permanently the segment has 396px and the trade no longer
// exists, so the shorter word is chosen on its own merits: it is what Sir calls
// the thing. The parameter keeps the domain's name for the kind; only the
// rendered word is short.
export function anomalyWord(kind: 'vaccinations' | 'unconfirmed', n: number): string {
  return kind === 'unconfirmed' ? 'unconfirmed' : `vaccine${n === 1 ? '' : 's'}`;
}
